import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/src/renderer/components/ui/Button'
import './OpenInAppPrompt.scss'

export interface OpenInAppPromptProps {
  onDismiss: () => void
}

/**
 * OpenInAppPrompt Component
 *
 * Shows a prompt on the web app when a password reset link is opened,
 * offering to open the Electron app instead of continuing in browser.
 */
export function OpenInAppPrompt({ onDismiss }: OpenInAppPromptProps) {
  const { t } = useTranslation()
  const [attemptedOpen, setAttemptedOpen] = useState(false)

  const handleOpenInApp = () => {
    // Get current URL with recovery token
    const currentHash = window.location.hash

    // Create deep link for Electron
    const deepLink = `happytask://password-reset${currentHash}`


    // Try to open Electron app
    window.location.href = deepLink

    setAttemptedOpen(true)

    // If app didn't open after 2 seconds, user can continue in browser
    setTimeout(() => {
      // App is still here, so Electron didn't open
    }, 2000)
  }

  // NOTE: Auto-open is now handled in SetupPage.tsx for immediate redirect
  // This component only shows as a fallback if the app doesn't open

  return (
    <div className="open-in-app-prompt">
      <div className="open-in-app-prompt__content">
        <h3 className="open-in-app-prompt__title">
          {t('auth.resetPassword.openInAppTitle')}
        </h3>
        <p className="open-in-app-prompt__subtitle">
          {t('auth.resetPassword.openInAppSubtitle')}
        </p>

        <div className="open-in-app-prompt__buttons">
          <Button
            variant="primary"
            onClick={handleOpenInApp}
          >
            {t('auth.resetPassword.openInApp')}
          </Button>
          <Button
            variant="secondary"
            onClick={onDismiss}
          >
            {attemptedOpen
              ? t('auth.resetPassword.continueInBrowser')
              : t('auth.resetPassword.stayInBrowser')}
          </Button>
        </div>
      </div>
    </div>
  )
}
